import React, { CSSProperties } from "react";
import { escapeRegExp } from "lodash";

/**
 * Pango text markup (https://docs.gtk.org/Pango/pango_markup.html) in the text_content, rendered by the backend
 * (image-composer TextRenderer) using Pango - here it is translated to SVG tspans so the editor shows the same.
 */

// same tags as image-composer TextRenderer::PANGO_MARKUP_REGEX:
const PANGO_TAG = '<\\/?(span|b|big|i|s|sub|sup|small|tt|u)(\\s[^>]*)?>'
const PANGO_ENTITY = '&(#x[0-9a-f]+|#[0-9]+|amp|lt|gt|quot|apos);'
const PANGO_MARKUP_REGEX = new RegExp(PANGO_TAG, 'i')
const PANGO_TOKEN_REGEX = new RegExp(`${PANGO_TAG}|${PANGO_ENTITY}`, 'i')

const PANGO_SCALE = 1024        // pango sizes are in 1024ths of a point, backend renders at 72 dpi so 1pt = 1px
const PANGO_SCALE_STEP = 1.2    // <big>, <small>, larger, smaller
const PANGO_SUB_SUP_SCALE = 1 / PANGO_SCALE_STEP

const NAMED_SIZES = {
    'xx-small': -3, 'x-small': -2, 'small': -1, 'medium': 0, 'large': 1, 'x-large': 2, 'xx-large': 3,
}
const NAMED_WEIGHTS = {
    thin: 100, ultralight: 200, light: 300, semilight: 350, book: 380, normal: 400, medium: 500,
    semibold: 600, bold: 700, ultrabold: 800, heavy: 900, ultraheavy: 1000,
}
const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }

export type PangoToken = {
    type: 'tag' | 'entity' | 'char'
    value: string
}

export type PangoRun = {
    text: string
    style: CSSProperties
    fill?: string
}

type PangoState = {
    scale: number           // font size relative to the layer font size (or to absoluteSize)
    absoluteSize?: number   // px
    style: CSSProperties
    fill?: string
    decorations: string[]
}

export const hasPangoMarkup = (text: string | null | undefined): boolean => {
    return typeof text === 'string' && PANGO_MARKUP_REGEX.test(text)
}

/**
 * Split the markup into tags, entities and single characters (code points, so emoji stay whole).
 */
export const tokenizePangoMarkup = (text: string): PangoToken[] => {
    const tokens: PangoToken[] = []
    const regex = new RegExp(PANGO_TOKEN_REGEX.source, 'gi')
    let last = 0
    let match: RegExpExecArray

    while ((match = regex.exec(text)) !== null) {
        Array.from(text.slice(last, match.index)).forEach(char => tokens.push({ type: 'char', value: char }))
        tokens.push({ type: match[0].startsWith('&') ? 'entity' : 'tag', value: match[0] })
        last = match.index + match[0].length
    }
    Array.from(text.slice(last)).forEach(char => tokens.push({ type: 'char', value: char }))

    return tokens
}

/**
 * Change the visible characters of the markup only (letter case transforms), keeping tags and entities intact.
 *
 * @param transform gets the character, the previous visible character and the index of the visible character
 */
export const mapPangoChars = (text: string, transform: (char: string, prevChar: string, index: number) => string): string => {
    let prevChar = ''
    let index = 0

    return tokenizePangoMarkup(text).map(token => {
        if (token.type === 'tag') {
            return token.value
        }
        if (token.type === 'entity') {
            prevChar = decodeEntity(token.value)
            index++
            return token.value
        }
        const result = transform(token.value, prevChar, index)
        prevChar = token.value
        index++
        return result
    }).join('')
}

/**
 * Put the separator between every visible character of the markup (vertical text), tags stay attached to the characters.
 */
export const splitPangoChars = (text: string, separator: string): string => {
    let isFirst = true

    return tokenizePangoMarkup(text).map(token => {
        if (token.type === 'tag') {
            return token.value
        }
        const result = isFirst ? token.value : separator + token.value
        isFirst = false
        return result
    }).join('')
}

/**
 * Make a part (line, word) of the markup valid on its own: reopen the tags left open by the previous parts
 * and close the tags left open at its end (see image-composer TextRenderer::balancePangoMarkup).
 *
 * @param openTags opening tags left open by the previous parts, updated by the tags of the text
 */
export const balancePangoMarkup = (text: string, openTags: string[]): string => {
    const prefix = openTags.join('')

    tokenizePangoMarkup(text).forEach(token => {
        if (token.type !== 'tag') {
            return
        }
        if (token.value.startsWith('</')) {
            openTags.pop()
        } else {
            openTags.push(token.value)
        }
    })

    const suffix = [...openTags].reverse().map(tag => `</${getTagName(tag)}>`).join('')

    return prefix + text + suffix
}

/**
 * Split the markup into words (not inside the tags, e.g. <span foreground="red">), each word valid on its own.
 */
export const splitPangoWords = (text: string): string[] => {
    const openTags: string[] = []

    return text.split(/\s+(?![^<]*>)/).map(word => balancePangoMarkup(word, openTags))
}

/**
 * Plain text without the markup.
 */
export const stripPangoMarkup = (text: string): string => {
    return parsePangoMarkup(text).map(run => run.text).join('')
}

/**
 * Position in the plain text of the given position in the markup (e.g. a cursor in the text_content with the tags),
 * a position inside a tag or an entity snaps to its start.
 */
export const getPangoTextIndex = (text: string, index: number): number => {
    let markupIndex = 0
    let textIndex = 0

    for (const token of tokenizePangoMarkup(text)) {
        if (markupIndex + token.value.length > index) {
            break
        }
        markupIndex += token.value.length
        if (token.type !== 'tag') {
            textIndex += token.type === 'entity' ? decodeEntity(token.value).length : token.value.length
        }
    }

    return textIndex
}

/**
 * Position in the markup of the given position in the plain text (e.g. a click into the rendered text), the opposite of getPangoTextIndex.
 * The position stays before the tags following it.
 */
export const getPangoMarkupIndex = (text: string, textIndex: number): number => {
    let markupIndex = 0
    let index = 0

    for (const token of tokenizePangoMarkup(text)) {
        if (index >= textIndex) {
            break
        }
        markupIndex += token.value.length
        if (token.type !== 'tag') {
            index += token.type === 'entity' ? decodeEntity(token.value).length : token.value.length
        }
    }

    return markupIndex
}

/**
 * Position of each rendered line in the plain text (whitespace between the words of a line may be collapsed).
 */
export const getPangoLineRanges = (plainText: string, lineTexts: string[]): { from: number, to: number }[] => {
    let pointer = 0

    return lineTexts.map(lineText => {
        const words = lineText.split(/\s+/).filter(w => w.length)
        const match = words.length ? new RegExp(words.map(escapeRegExp).join('\\s+')).exec(plainText.substring(pointer)) : null

        let from = pointer
        if (match) {
            from = pointer + match.index
            pointer = from + match[0].length
        } else if (plainText[pointer] === "\n") {
            from = pointer = pointer + 1   //blank line right after the line break
        }

        return { from, to: from + lineText.length }
    })
}

/**
 * Parse the markup into runs of text with the same style. Unknown attributes are ignored.
 */
export const parsePangoMarkup = (text: string): PangoRun[] => {
    const runs: { text: string, state: PangoState }[] = []
    const stack: PangoState[] = [{ scale: 1, style: {}, decorations: [] }]

    tokenizePangoMarkup(text).forEach(token => {
        const state = stack[stack.length - 1]

        if (token.type === 'tag') {
            if (token.value.startsWith('</')) {
                if (stack.length > 1) {
                    stack.pop()
                }
            } else {
                stack.push(applyTag(state, token.value))
            }
            return
        }

        const char = token.type === 'entity' ? decodeEntity(token.value) : token.value
        const lastRun = runs[runs.length - 1]
        if (lastRun && lastRun.state === state) {
            lastRun.text += char
        } else {
            runs.push({ text: char, state })
        }
    })

    return runs.map(({ text, state }) => ({
        text,
        style: getStateStyle(state),
        fill: state.fill,
    }))
}

/**
 * Render the markup as tspans (to be placed inside a <text> or a line <tspan>).
 *
 * @param withColors false for the stroke underlayer, which is filled by the stroke color
 */
export const renderPangoMarkup = (text: string, keyPrefix: string, withColors: boolean = true): JSX.Element[] => {
    return parsePangoMarkup(text).map((run, i) => <tspan
        key={`${keyPrefix}-${i}`}
        style={withColors ? run.style : { ...run.style, fillOpacity: undefined }}
        fill={withColors ? run.fill : undefined}
    >{run.text}</tspan>)
}

const getTagName = (tag: string): string => {
    return tag.match(/^<\/?([a-z]+)/i)[1].toLowerCase()
}

const decodeEntity = (entity: string): string => {
    const name = entity.slice(1, -1).toLowerCase()

    if (name.startsWith('#x')) {
        return String.fromCodePoint(parseInt(name.slice(2), 16))
    }
    if (name.startsWith('#')) {
        return String.fromCodePoint(parseInt(name.slice(1), 10))
    }

    return ENTITIES[name] ?? entity
}

const getAttributes = (tag: string): Record<string, string> => {
    const attributes: Record<string, string> = {}
    const regex = /([a-z_]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/gi
    let match: RegExpExecArray

    while ((match = regex.exec(tag)) !== null) {
        attributes[match[1].toLowerCase()] = match[2] ?? match[3]
    }

    return attributes
}

const applyTag = (parent: PangoState, tag: string): PangoState => {
    const state: PangoState = { ...parent, style: { ...parent.style }, decorations: [...parent.decorations] }

    switch (getTagName(tag)) {
        case 'b':
            state.style.fontWeight = 'bold'
            break
        case 'big':
            state.scale *= PANGO_SCALE_STEP
            break
        case 'i':
            state.style.fontStyle = 'italic'
            break
        case 's':
            state.decorations.push('line-through')
            break
        case 'sub':
            state.scale *= PANGO_SUB_SUP_SCALE
            state.style.baselineShift = 'sub'
            break
        case 'sup':
            state.scale *= PANGO_SUB_SUP_SCALE
            state.style.baselineShift = 'super'
            break
        case 'small':
            state.scale /= PANGO_SCALE_STEP
            break
        case 'tt':
            state.style.fontFamily = 'monospace'
            break
        case 'u':
            state.decorations.push('underline')
            break
        case 'span':
            applySpanAttributes(state, getAttributes(tag))
            break
    }

    return state
}

const applySpanAttributes = (state: PangoState, attributes: Record<string, string>): void => {
    Object.keys(attributes).forEach(name => {
        const value = attributes[name]
        const lowerValue = value.trim().toLowerCase()

        switch (name) {
            case 'face':
            case 'font_family':
                state.style.fontFamily = `"${value}"`
                break
            case 'size':
            case 'font_size':
                applySize(state, lowerValue)
                break
            case 'style':
            case 'font_style':
                if (['normal', 'italic', 'oblique'].includes(lowerValue)) {
                    state.style.fontStyle = lowerValue as CSSProperties['fontStyle']
                }
                break
            case 'weight':
            case 'font_weight':
                state.style.fontWeight = NAMED_WEIGHTS[lowerValue] ?? (/^\d+$/.test(lowerValue) ? Number(lowerValue) : state.style.fontWeight)
                break
            case 'variant':
            case 'font_variant':
                state.style.fontVariant = lowerValue === 'smallcaps' ? 'small-caps' : lowerValue
                break
            case 'stretch':
            case 'font_stretch':
                state.style.fontStretch = lowerValue.replace(/^(ultra|extra|semi)/, '$1-')
                break
            case 'foreground':
            case 'fgcolor':
            case 'color':
                state.fill = value
                break
            case 'alpha':
            case 'fgalpha':
                state.style.fillOpacity = lowerValue.endsWith('%') ? parseFloat(lowerValue) / 100 : parseInt(lowerValue, 10) / 65535
                break
            case 'underline':
                state.decorations = state.decorations.filter(d => d !== 'underline')
                if (lowerValue !== 'none') {
                    state.decorations.push('underline')
                }
                break
            case 'strikethrough':
                state.decorations = state.decorations.filter(d => d !== 'line-through')
                if (lowerValue === 'true') {
                    state.decorations.push('line-through')
                }
                break
            case 'rise':
            case 'baseline_shift':
                if (/^-?\d+$/.test(lowerValue)) {
                    state.style.baselineShift = `${parseInt(lowerValue, 10) / PANGO_SCALE}px`
                } else if (['superscript', 'subscript'].includes(lowerValue)) {
                    state.style.baselineShift = lowerValue === 'superscript' ? 'super' : 'sub'
                }
                break
            case 'letter_spacing':
                state.style.letterSpacing = `${parseInt(lowerValue, 10) / PANGO_SCALE}px`
                break
            case 'text_transform':
                state.style.textTransform = lowerValue as CSSProperties['textTransform']
                break
        }
    })
}

const applySize = (state: PangoState, value: string): void => {
    if (value in NAMED_SIZES) {
        state.absoluteSize = undefined
        state.scale = Math.pow(PANGO_SCALE_STEP, NAMED_SIZES[value])
    } else if (value === 'larger') {
        state.scale *= PANGO_SCALE_STEP
    } else if (value === 'smaller') {
        state.scale /= PANGO_SCALE_STEP
    } else if (value.endsWith('%')) {
        state.scale *= parseFloat(value) / 100
    } else if (/^\d+(\.\d+)?(pt|px)$/.test(value)) {
        state.absoluteSize = parseFloat(value)
        state.scale = 1
    } else if (/^\d+$/.test(value)) {
        state.absoluteSize = parseInt(value, 10) / PANGO_SCALE
        state.scale = 1
    }
}

const getStateStyle = (state: PangoState): CSSProperties => {
    const style: CSSProperties = { ...state.style }

    if (state.absoluteSize) {
        style.fontSize = `${state.absoluteSize * state.scale}px`
    } else if (state.scale !== 1) {
        style.fontSize = `${state.scale}em`
    }
    if (state.decorations.length) {
        style.textDecoration = [...new Set(state.decorations)].join(' ')
    }

    return style
}
